/**
 * formatTime — the one way the Facilitator Portal shows a time of day.
 *
 * Product ruling 29 Sep: always show the zone after the time
 * ("18:00 WIB"), and both zones when another zone is in play
 * ("18:00 WIB · 19:00 SGT"). An unlabelled time is misread across a
 * Singapore / Indonesia team.
 *
 * 24-hour in both languages (Nino, 12 Oct, via Design: "more universal than
 * the am/pm format"): English "16:00", Bahasa "16.00".
 *
 * The first zone is this device's (the clock the facilitator is looking
 * at). The second is the facilitator's profile zone, shown only when it
 * gives a different clock time, e.g. while travelling. Sessions carry no
 * zone of their own yet; `otherZone` takes one when they do.
 *
 * Indonesian (1 Oct): with a dot, "18.00 WIB", in the portal's chosen
 * language (lib/i18n).
 */
import { currentLocale } from '@/lib/i18n';

// Zone names people use here; anything else falls back to the browser's short name.
const ZONE_NAMES: Record<string, string> = {
  'Asia/Jakarta': 'WIB',
  'Asia/Pontianak': 'WIB',
  'Asia/Makassar': 'WITA',
  'Asia/Jayapura': 'WIT',
  'Asia/Singapore': 'SGT',
  'Asia/Kuala_Lumpur': 'MYT',
  'Asia/Manila': 'PHT',
  'Asia/Bangkok': 'ICT',
  'Asia/Ho_Chi_Minh': 'ICT',
  UTC: 'UTC',
  'Etc/UTC': 'UTC',
};

let homeZone: string | null = null;

/** The facilitator's profile zone. "UTC" is the server default for "never set", so it is ignored. */
export function setHomeZone(tz: string | null | undefined): void {
  homeZone = tz && tz !== 'UTC' && tz !== 'Etc/UTC' ? tz : null;
}

export function deviceZone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

/** "WIB", "SGT", or the browser's short name (e.g. "GMT+7", "BST"). */
export function zoneLabel(tz: string, at: Date = new Date()): string {
  if (ZONE_NAMES[tz]) return ZONE_NAMES[tz];
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName');
    return part?.value ?? tz;
  } catch { return tz; }
}

function clock(d: Date, tz: string, seconds: boolean): string {
  const id = currentLocale() === 'id';
  return d.toLocaleTimeString(id ? 'id-ID' : 'en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' as const } : {}),
    hourCycle: 'h23',
  });
}

/** A date in the portal's language: "Fri, Oct 2" / "Jum, 2 Okt". */
export function formatDate(value: string | Date, opts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' }): string {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(currentLocale() === 'id' ? 'id-ID' : 'en-US', opts);
}

interface Options {
  /** Show seconds ("18:00:15 WIB"). */
  seconds?: boolean;
  /** A zone to show second, instead of the facilitator's profile zone. */
  otherZone?: string | null;
}

/** "18:00 WIB", or "18:00 WIB · 19:00 SGT" when another zone reads differently.
 *  The zone never drops to a line of its own (Content Authoring, 28 Oct). */
export function formatTime(value: string | number | Date, opts: Options = {}): string {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const seconds = !!opts.seconds;
  const here = deviceZone();
  const hereClock = clock(d, here, seconds);
  const first = `${hereClock}\u00a0${zoneLabel(here, d)}`;
  const other = opts.otherZone ?? homeZone;
  if (!other) return first;
  const otherClock = clock(d, other, seconds);
  if (otherClock === hereClock) return first;
  return `${first} · ${otherClock}\u00a0${zoneLabel(other, d)}`;
}

/** "18:00 – 18:45 WIB", or "18:00 – 18:45 WIB · 19:00 – 19:45 SGT". */
export function formatTimeRange(start: string | Date, end: string | Date, opts: Options = {}): string {
  const s = start instanceof Date ? start : new Date(start);
  const e = end instanceof Date ? end : new Date(end);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return `${formatTime(start, opts)} – ${formatTime(end, opts)}`;
  const seconds = !!opts.seconds;
  const range = (tz: string) => {
    const a = clock(s, tz, seconds);
    const b = clock(e, tz, seconds);
    return `${a} – ${b}\u00a0${zoneLabel(tz, s)}`;
  };
  const here = deviceZone();
  const first = range(here);
  const other = opts.otherZone ?? homeZone;
  if (!other || clock(s, other, seconds) === clock(s, here, seconds)) return first;
  return `${first} · ${range(other)}`;
}

/**
 * The same range split in two, for layouts that put the second zone on its
 * own smaller line instead of letting one long string wrap (Design, 1 Oct).
 */
export function formatTimeRangeParts(start: string | Date, end: string | Date, opts: Options = {}): { here: string; other: string | null } {
  const [here, other = null] = formatTimeRange(start, end, opts).split(' · ');
  return { here, other };
}

/** Just this device's zone name, for headings like "Times in WIB". */
export function currentZoneLabel(): string {
  return zoneLabel(deviceZone());
}
